import {
  AfterContentInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  UntypedFormControl,
  FormControlName,
  FormControlDirective,
  AbstractControl,
  ControlContainer,
} from '@angular/forms';
import {BehaviorSubject, Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {hasRequiredValidator} from '../../utils';
import {ValidationService} from '../../services/validation.service';
import {ErrorProviderDirective, ErrorsComponent} from '../errors/errors.component';
import {AsyncPipe} from '@angular/common';
import {ensureArray} from '@matchsource/utils';
import {FieldContainer} from '../../declarations';
import {FIELD_CONTAINER_TOKEN} from '../../constants';
import {ButtonDirective} from '../../directives/button/button.directive';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

interface FieldClassList {
  marked?: boolean;

  [key: string]: boolean;
}

interface WrapperClassList {
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  active?: boolean;

  [key: string]: boolean;
}

type Mode = 'standard' | 'simple';

const MODE_CLASS_MAP: Record<Mode, string> = {
  simple: 'simple-field',
  standard: '',
};

type Layout = 'horizontal' | 'vertical';

const LAYOUT_CLASS_MAP: Record<Layout, string> = {
  horizontal: 'horizontal',
  vertical: '',
};

type Order = 'reverse' | 'standard';

const ORDER_CLASS_MAP: Record<Order, string> = {
  reverse: 'reverse',
  standard: '',
};

@Component({
  selector: 'ms-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, ButtonDirective],
  providers: [
    {
      provide: FIELD_CONTAINER_TOKEN,
      useExisting: forwardRef(() => FormFieldComponent),
    },
  ],
})
export class FormFieldComponent implements AfterContentInit, FieldContainer {
  readonly fieldClass = input<string>();
  readonly wrapperClass = input<string>();
  readonly fieldNoteClass = input<string>();
  readonly label = input<string>();
  readonly hideLabel = input(false, {transform: booleanAttribute});
  readonly hasLabelTemplate = input(false);
  readonly note = input<string>();
  readonly highlight = input(false);
  readonly mode = input<Mode>('standard');
  readonly layout = input<Layout>('vertical');
  readonly order = input<Order>('standard');
  readonly multilineLabel = input(false);
  readonly hideNote = input(false, {transform: booleanAttribute});
  readonly hasExtraAction = input(false, {transform: booleanAttribute});
  readonly extraActionDisabled = input(false, {transform: booleanAttribute});
  readonly fieldFormControl = input<AbstractControl | null>(null);
  readonly controlName = input<string>();
  readonly extraActionLabel = input<string>();

  readonly extraAction = output<void>();

  readonly formControlNameDirective = contentChild(FormControlName);
  readonly formControlDirective = contentChild(FormControlDirective);
  readonly customErrorsComponent = contentChild(ErrorsComponent);
  readonly charactersCounter = viewChild<ElementRef<HTMLElement>>('charactersCounter');

  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly validation = inject(ValidationService);
  private readonly controlContainer = inject(ControlContainer, {optional: true});
  private readonly errorProvider = inject(ErrorProviderDirective, {optional: true});
  private readonly destroyRef = inject(DestroyRef);

  errorMessageEmitter: BehaviorSubject<Observable<string | null>> = new BehaviorSubject(null);
  errorMessage$: Observable<string | null>;

  private touched = false;

  private readonly overriddenCustomErrorsComponentList: ErrorsComponent[] = [];

  private readonly disabled = signal(false);
  private readonly required = signal(false);
  private readonly active = signal(false);

  readonly isInvalid = signal(false);
  readonly hasCharacterCounter = signal(false);

  readonly showLabel = computed(() => !this.hideLabel() && !this.hasLabelTemplate());
  readonly showNote = computed(() => !this.hideNote());
  readonly fieldClassList = computed<FieldClassList>(() => {
    const classes: FieldClassList = {marked: this.required() && !this.disabled()};
    const fieldClass = this.fieldClass();

    if (fieldClass) {
      classes[fieldClass] = true;
    }

    return classes;
  });
  readonly wrapperClassList = computed<WrapperClassList>(() => {
    const classes: WrapperClassList = {
      disabled: this.disabled(),
      required: this.required() && !this.disabled(),
      invalid: this.isInvalid(),
      active: this.active(),
    };

    const wrapperClass = this.wrapperClass();
    const modeClass = MODE_CLASS_MAP[this.mode()];
    const layoutClass = LAYOUT_CLASS_MAP[this.layout()];
    const orderClass = ORDER_CLASS_MAP[this.order()];

    if (wrapperClass) {
      classes[wrapperClass] = true;
    }

    if (modeClass) {
      classes[modeClass] = true;
    }

    if (layoutClass) {
      classes[layoutClass] = true;
    }

    if (orderClass) {
      classes[orderClass] = true;
    }

    return classes;
  });

  constructor() {
    this.errorMessage$ = this.errorMessageEmitter.pipe(switchMap(message$ => message$));
    this.handleHighlightChanges();
  }

  get control(): AbstractControl | null {
    const controlName = this.controlName();

    if (controlName && this.controlContainer) {
      return this.controlContainer.control?.get(controlName) ?? null;
    }

    const fieldFormControl = this.fieldFormControl();

    if (fieldFormControl) {
      return fieldFormControl;
    }

    const formControlNameDirective = this.formControlNameDirective();

    if (formControlNameDirective) {
      return formControlNameDirective.control;
    }

    const formControlDirective = this.formControlDirective();

    if (formControlDirective) {
      return formControlDirective.control;
    }

    return null;
  }

  private get customErrors(): ErrorsComponent | ErrorsComponent[] | null {
    if (this.overriddenCustomErrorsComponentList.length) {
      return this.overriddenCustomErrorsComponentList;
    }

    const customErrorsComponent = this.customErrorsComponent();

    if (customErrorsComponent) {
      return customErrorsComponent;
    }

    return this.errorProvider?.customErrors() ?? null;
  }

  ngAfterContentInit(): void {
    const {control} = this;

    if (control) {
      this.initControlHandlers(control);
    }
  }

  setCustomErrors(errorsComponent: ErrorsComponent): void {
    this.overriddenCustomErrorsComponentList.push(errorsComponent);
  }

  setHasCharacterCounter(value: boolean): void {
    this.hasCharacterCounter.set(value);
  }

  onFocus(): void {
    this.active.set(true);
  }

  onBlur(): void {
    this.active.set(false);

    const {control} = this;

    if (control && (control.pristine || (control.touched && !this.touched))) {
      this.touched = control.touched;
      this.updateState();
    }
  }

  onExtraActionClick() {
    this.extraAction.emit();
  }

  private initControlHandlers(control: AbstractControl | UntypedFormControl): void {
    this.required.set(hasRequiredValidator(control));

    (control as any).registerOnDisabledChange?.((isDisabled: boolean) => {
      this.updateState();

      if (!isDisabled) {
        this.touched = control.touched;
      }
    });

    control.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateState();
    });

    this.updateState();
  }

  private updateState(): void {
    const control = this.control;

    if (!control) {
      return;
    }

    this.disabled.set(control.disabled);
    this.required.set(hasRequiredValidator(control));
    this.isInvalid.set((control.touched && control.invalid) || (this.highlight() && control.invalid));

    this.errorMessageEmitter.next(this.getErrorMessage(control));

    this.cdRef.markForCheck();
  }

  private getErrorMessage(control: AbstractControl): Observable<string> {
    if (this.customErrors) {
      const customErrorsList = ensureArray(this.customErrors);
      for (const customErrorsProvider of customErrorsList) {
        const errorMessage = customErrorsProvider.getMessage(control.errors);

        if (errorMessage !== null) {
          return errorMessage;
        }
      }
    }

    return this.validation.getErrorMessage(control.errors);
  }

  private handleHighlightChanges(): void {
    effect(() => {
      this.highlight();
      this.updateState();
    });
  }
}
